/**
 * Parameter Drift Mock
 *
 * Shows how parameter values drift across WFA windows,
 * with CV and stability classification for each parameter.
 */

import { LineChart, Line, ResponsiveContainer } from 'recharts';

const windows = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8'];

const params = [
  {
    name: 'Fast EMA',
    values: [14, 15, 14, 13, 15, 14, 16, 14],
    mean: 14.4,
    cv: 6.2,
    status: 'Stable',
    statusColor: '#22c55e',
  },
  {
    name: 'Slow EMA',
    values: [28, 30, 27, 29, 31, 28, 30, 29],
    mean: 29.0,
    cv: 4.8,
    status: 'Stable',
    statusColor: '#22c55e',
  },
  {
    name: 'Stop Loss %',
    values: [2.0, 2.5, 2.0, 3.0, 2.5, 1.5, 3.5, 2.0],
    mean: 2.4,
    cv: 24.1,
    status: 'Conditional',
    statusColor: '#f59e0b',
  },
  {
    name: 'RSI Period',
    values: [14, 22, 10, 28, 16, 35, 12, 24],
    mean: 20.1,
    cv: 40.5,
    status: 'Unstable',
    statusColor: '#ef4444',
  },
];

export default function ParameterDriftMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Parameter Drift Across WFA Windows
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem 1rem' }}>
        {/* Header */}
        <div style={{
          display: 'flex', gap: '0.375rem', fontSize: '0.5rem', color: '#64748b',
          marginBottom: '0.4rem', fontFamily: 'ui-monospace, monospace',
          borderBottom: '1px solid #334155', paddingBottom: '0.25rem',
        }}>
          <span style={{ width: 65 }}>Parameter</span>
          <span style={{ width: 40, textAlign: 'center' }}>Mean</span>
          <span style={{ flex: 1, textAlign: 'center' }}>Drift ({windows.length} windows)</span>
          <span style={{ width: 36, textAlign: 'center' }}>CV</span>
          <span style={{ width: 62, textAlign: 'right' }}>Status</span>
        </div>

        {params.map((p) => (
          <div key={p.name} style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            marginBottom: '0.5rem',
          }}>
            <span style={{ width: 65, fontSize: '0.5625rem', color: '#e5e7eb', fontWeight: 600 }}>
              {p.name}
            </span>
            <span style={{
              width: 40, fontSize: '0.5625rem', color: '#94a3b8',
              textAlign: 'center', fontFamily: 'ui-monospace, monospace',
            }}>
              {typeof p.mean === 'number' && p.mean % 1 !== 0 ? p.mean.toFixed(1) : p.mean}
            </span>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 80, height: 18 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={p.values.map((v, i) => ({ w: i, v }))} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line type="monotone" dataKey="v" stroke={p.statusColor} strokeWidth={1.2} dot={{ r: 1.5, fill: p.statusColor }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <span style={{
              width: 36, fontSize: '0.5625rem', textAlign: 'center',
              color: p.statusColor, fontWeight: 600,
              fontFamily: 'ui-monospace, monospace',
            }}>
              {p.cv}%
            </span>
            <span style={{
              width: 62, textAlign: 'right', fontSize: '0.5rem',
              fontWeight: 700, color: p.statusColor,
              background: `${p.statusColor}12`,
              padding: '0.1rem 0.3rem',
              borderRadius: 3,
            }}>
              {p.status}
            </span>
          </div>
        ))}

        {/* Summary */}
        <div style={{
          marginTop: '0.25rem', paddingTop: '0.375rem',
          borderTop: '1px solid #334155',
          display: 'flex', justifyContent: 'center', gap: '1.5rem',
          fontSize: '0.5rem', color: '#94a3b8',
        }}>
          <span>Stable: <strong style={{ color: '#22c55e' }}>50%</strong> (2/4)</span>
          <span>Conditional: <strong style={{ color: '#f59e0b' }}>25%</strong> (1/4)</span>
          <span>Unstable: <strong style={{ color: '#ef4444' }}>25%</strong> (1/4)</span>
        </div>
      </div>
      <div className="platform-mock-caption">
        EMA parameters are stable across windows. RSI period drifts significantly — consider fixing it.
      </div>
    </div>
  );
}
